/**
 * Ramer–Douglas–Peucker thinning for outgoing ink.
 *
 * A pointer emits far more points than a line needs: at 20 Hz a quick stroke
 * is a few dozen samples, most of them on a straight run. We draw locally at
 * full resolution and send the thinned version — the shape people see is the
 * same, the packets are a fraction of the size.
 *
 * ⚠️ Frame units are not square: x spans the frame width and y its height, so
 * on a wide frame one unit of y is fewer pixels than one unit of x. The
 * distortion is under a factor of two for any normal screen and it only makes
 * the thinning slightly keener vertically, which nobody can see in ink.
 */

/** Default tolerance in frame units — roughly two or three pixels of screen. */
export const SIMPLIFY_EPSILON = 5;

/**
 * Thin a flat [x0, y0, x1, y1, …] run. First and last points always survive:
 * a stroke that loses its ends stops meeting the one before it.
 */
export function simplify(
  flat: number[],
  epsilon: number = SIMPLIFY_EPSILON,
): number[] {
  const count = Math.floor(flat.length / 2);
  if (count < 3 || epsilon <= 0) return flat.slice(0, count * 2);

  const keep = new Uint8Array(count);
  keep[0] = 1;
  keep[count - 1] = 1;

  // Iterative rather than recursive: a long stroke should not risk the stack.
  const stack: Array<[number, number]> = [[0, count - 1]];

  while (stack.length) {
    const [first, last] = stack.pop()!;
    if (last <= first + 1) continue;

    let worst = -1;
    let worstDistance = 0;

    for (let i = first + 1; i < last; i++) {
      const distance = perpendicular(flat, i, first, last);
      if (distance > worstDistance) {
        worstDistance = distance;
        worst = i;
      }
    }

    if (worst !== -1 && worstDistance > epsilon) {
      keep[worst] = 1;
      stack.push([first, worst], [worst, last]);
    }
  }

  const out: number[] = [];
  for (let i = 0; i < count; i++) {
    if (keep[i]) out.push(flat[i * 2], flat[i * 2 + 1]);
  }
  return out;
}

function perpendicular(
  flat: number[],
  index: number,
  first: number,
  last: number,
): number {
  const px = flat[index * 2];
  const py = flat[index * 2 + 1];
  const ax = flat[first * 2];
  const ay = flat[first * 2 + 1];
  const bx = flat[last * 2];
  const by = flat[last * 2 + 1];

  const dx = bx - ax;
  const dy = by - ay;
  const length = Math.hypot(dx, dy);

  // Degenerate segment (a loop back to the same point): fall back to distance
  // from the anchor, otherwise every point on a circle looks "on the line".
  if (length === 0) return Math.hypot(px - ax, py - ay);

  return Math.abs(dy * px - dx * py + bx * ay - by * ax) / length;
}
